import React from 'react';
import { Button, Flex, Select, Text } from '@chatsdet/ui';
import { RunHistoryCard } from './RunHistoryCard';
import type { Pagination, WorkflowRunSummaryDto } from '../hooks/useWorkflowRuns';

const pageSizeOptions = [5, 10, 20, 50];

export function RunHistoryList({
  runs,
  pagination,
  isLoading,
  selectedRunId,
  onSelectRun,
  onDeleteRun,
  onPageChange,
  onPageSizeChange,
}: {
  runs: WorkflowRunSummaryDto[];
  pagination: Pagination;
  isLoading: boolean;
  selectedRunId: string | null;
  onSelectRun: (runId: string) => void;
  onDeleteRun: (runId: string) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}) {
  const canPrev = pagination.page > 1 && !isLoading;
  const canNext =
    pagination.totalPages === 0 ? false : pagination.page < pagination.totalPages && !isLoading;

  const items = React.useMemo(
    () =>
      pageSizeOptions.map((size) => ({
        id: String(size),
        label: String(size),
      })),
    []
  );

  return (
    <div className="flex flex-col">
      {runs.length === 0 && !isLoading ? (
        <div className="px-4 py-8 text-center">
          <Text className="text-sm text-muted-foreground">No runs yet</Text>
          <Text size="xs" className="text-muted-foreground mt-1">
            Start a run to see history here.
          </Text>
        </div>
      ) : (
        <div className="divide-y divide-border/50">
          {runs.map((run) => (
            <div key={run.id} className="group">
              <RunHistoryCard
                run={run}
                isSelected={selectedRunId === run.id}
                onSelect={onSelectRun}
                onDelete={onDeleteRun}
              />
            </div>
          ))}
        </div>
      )}

      {/* Pagination - simplified */}
      <div className="pt-3 px-4 pb-3 border-t border-border/50">
        <Flex align="center" justify="between" gap="sm">
          <Flex align="center" gap="xs">
            <Button
              size="sm"
              variant="ghost"
              isDisabled={!canPrev}
              onPress={() => onPageChange(Math.max(1, pagination.page - 1))}
            >
              Prev
            </Button>
            <Button
              size="sm"
              variant="ghost"
              isDisabled={!canNext}
              onPress={() => onPageChange(pagination.page + 1)}
            >
              Next
            </Button>
            <Text size="xs" className="text-muted-foreground">
              {pagination.page} / {Math.max(1, pagination.totalPages)}
            </Text>
          </Flex>
          <Select
            aria-label="Runs page size"
            selectedKey={String(pagination.pageSize)}
            onSelectionChange={(key) => {
              const next = Number.parseInt(String(key), 10);
              if (Number.isFinite(next) && next > 0) onPageSizeChange(next);
            }}
            items={items}
            className="w-[72px]"
            isDisabled={isLoading}
          />
        </Flex>
      </div>
    </div>
  );
}
